/**
 * Clients — the tenant client roster list (Kith TABLE-STAKES TS-1) +
 * "+ Add client" (TS-4). Lives at /clients/all since R9 (founder
 * live-walk 2026-07-06) made the task-first landing the /clients door;
 * the landing's "All clients →" link points here, and a viewer with a
 * caseload (whoami.has_caseload) gets the door back — a quiet "Your
 * day →" link that re-enters the ClientsLandingGate at /clients. Row
 * click → /clients/:clientId.
 *
 * Gate: page + fetch gate on canViewAnyClientRoster: every client
 * (canReadEveryClient: view_all_clinical, or the engagement lane under
 * the tenant's Everyone on staff setting) or the engagement lane
 * (view_engagement_clients with a roster flag; ADR-0053). A lane
 * viewer's list is the server's lane-filtered roster, the same clients
 * her record reads open. The
 * list endpoint re-derives the same gate server-side (a denied role
 * fires NO request — the PageShell access-as-prop contract keeps hooks
 * above the gate, React #310 discipline).
 *
 * House patterns: PageShell (standard xl container; the list is a paper
 * card, not a banded surface), AsyncBoundary for the primary query,
 * FilterPill + BottomSheet single-select pickers for the sort/filter bar
 * (the FilterBar grammar, single-select), Dialog + Button for the
 * create form, with the house fields (Field, TextInput, SelectField).
 * Tokens only.
 *
 * Client-roster founder pass (2026-07-11): rows carry unit (derived
 * server-side from the CM's staff_roster.unit_id → units.name), status
 * chip (Active affirm / Archived neutral), engagement chip (shown only
 * when SPICE is involved — a "Care management" chip on every row would
 * be the pill wall Design Pass 2 retired), and Added/Updated dates.
 * Sort (Name / Date added / Last updated) + filters (CM / Unit / Status)
 * run CLIENT-SIDE: the list read ships the full roster (no server
 * pagination; the workspace-list precedent). The old include-archived
 * pill became the Status filter; Archived/All fetch with
 * include_archived=1 and narrow locally. The view tabs (All clients,
 * Care management, SPICE) stand in for an engagement filter.
 * The name search narrows the current view, client-side, matching the
 * client's name or a care manager's (a therapist's in SPICE). It lives in
 * component state only; the URL carries the view alone, so leaving the
 * page or backing out of a record starts the search empty, and a view
 * switch keeps it.
 *
 * Backend: GET /v1/clients (repos/clients.ts listClients — ONE
 * withClinicalRead 'client_roster' audit row per view, caseload.ts
 * posture). Create: POST /v1/clients with first, last and preferred name,
 * the service, the care manager or SPICE therapist, pronouns and an
 * optional rate set; the route derives the display name, and the page
 * opens the new record.
 */

import { Fragment, useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { matchesSearch, searchQuery } from '@/lib/nameSearch';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Plus, X } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import {
  canManageClientIdentity,
  canManageClientRates,
  canReadEveryClient,
  canViewAnyClientRoster,
} from '@/lib/capabilities';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { readRateSets } from '@/lib/rateSets';
import { localDayOf } from '@/lib/proseDate';
import {
  clientHasCareManager,
  clientPhotoUrl,
  useClientsList,
  useCreateClient,
  useSpiceClientsList,
  type ClientEngagementValue,
  type ClientRosterRowWire,
  type SpiceClientRosterRowWire,
  type SpiceClientRosterScheduleWire,
} from '@/lib/clientsApi';
import {
  compareSpiceRosterRows,
  formatSpiceLastSession,
  formatSpiceThisMonth,
  type SpiceRosterSchedule,
  type SpiceRosterSortMode,
} from '@/lib/spiceRosterShape';
import { effectiveHold } from '@/lib/spiceHold';
import { formatSpiceAway } from '@/lib/spiceScheduleCopy';
import { practiceTodayIso } from '@/lib/practiceDate';
import { SpiceScheduleSummary } from '@/components/clientRecord/SpiceScheduleSummary';
import type { WhoamiResponse } from '@/lib/types';
import { ENGAGEMENT_LABELS } from '@/components/clientRecord/VitalsBand';
import { CLIENT_PRONOUN_VALUES } from '@/lib/noteVocabulary';
import { useAssignOptions } from '@/lib/workspace';
import { clientCreateRefusal, type RefusalBox } from '@/lib/clientCreateRefusals';
import { addClientBody, duplicateClient, showsCareManager, showsSpiceTherapist } from '@/lib/addClient';
import { focusFirstGap } from '@/lib/formGaps';
import { pronounForms } from '@/lib/pronounForms';
import { lifecycleBadge, type ClientPronouns } from '@/lib/clientRecord';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';
import { formatTimestamp } from '@/lib/format';
import { NavArrowLink } from '@/components/home/NavArrowLink';
import { PageShell } from '@/components/primitives/PageShell';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { BottomSheet } from '@/components/primitives/BottomSheet';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import { Dialog } from '@/components/primitives/Dialog';
import { Field, fieldBoxAria } from '@/components/primitives/Field';
import { SelectField } from '@/components/primitives/SelectField';
import { FLD } from '@/components/primitives/modalFormStyles';
import { FilterPill } from '@/components/primitives/FilterPill';
import { SearchField } from '@/components/primitives/SearchField';
import { TAB_RAIL_CLASSES } from '@/components/primitives/tabRail';
import { byDisplayName } from '@/lib/clientOrder';
import { TextInput } from '@/components/primitives/TextInput';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * The roster avatar — the client-photos seam (wired by 0142, the humanity
 * upgrade). Renders the photo when the wire carries one (photoUrl, passed
 * when the row's has_photo hint is true), falling back to the initials
 * monogram on none or any load error (the CmPhoto photo+fallback discipline;
 * a missing photo is never a broken image).
 */
export function ClientAvatar({
  name,
  photoUrl,
}: {
  name: string;
  photoUrl?: string | null;
}): ReactNode {
  const [errored, setErrored] = useState(false);
  if (photoUrl && !errored) {
    return (
      <img
        src={photoUrl}
        alt=""
        loading="lazy"
        data-testid="client-avatar-img"
        className="h-10 w-10 shrink-0 rounded-full object-cover bg-sage-100"
        onError={() => setErrored(true)}
      />
    );
  }
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sage-800 font-display text-[13px] font-semibold text-cream">
      {initials(name) || '?'}
    </span>
  );
}

// ---------------------------------------------------------------------
// Sort + filter vocabulary (client-side; the full roster ships on the
// wire). Values are stable keys; labels are the readable words the
// audience ruling asks for (no icon-only controls).
// ---------------------------------------------------------------------

type StatusFilter = 'active' | 'archived' | 'all';
type SortMode = 'name' | 'added' | 'updated';

const STATUS_OPTIONS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All (active + archived)' },
];

const SORT_OPTIONS: ReadonlyArray<{ value: SortMode; label: string }> = [
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'added', label: 'Date added (newest first)' },
  { value: 'updated', label: 'Last updated (newest first)' },
];

/** The sentinel CM-filter value for "no care manager assigned" rows —
 *  the operational gap an owner filters FOR. */
const CM_NONE = 'none';

// The roster search's rule lives in src/lib/nameSearch.ts, shared with
// the top search so both find the same clients.

/** A Clients-view row's searched fields: the client, the primary care
 *  manager, and every additional care manager. */
function clientSearchFields(c: ClientRosterRowWire): (string | null)[] {
  return [c.display_name, c.primary_cm_name, ...c.additional_care_managers.map((p) => p.name)];
}

/** The therapists a SPICE-view row's sessions name for themselves, each
 *  once: a therapist the client has only through a session. */
function sessionTherapists(c: Pick<SpiceClientRosterRowWire, 'schedule'>): Array<{ roster_id: string; name: string }> {
  const byId = new Map<string, string>();
  for (const s of c.schedule?.sessions ?? []) {
    if ((s.therapist_roster_id ?? null) !== null && s.therapist_name) byId.set(s.therapist_roster_id as string, s.therapist_name);
  }
  return [...byId.entries()].map(([roster_id, name]) => ({ roster_id, name }));
}

/** A SPICE-view row's searched fields: the client, the therapist, every
 *  additional therapist, and every therapist a session names. */
function spiceSearchFields(c: SpiceClientRosterRowWire): (string | null)[] {
  return [
    c.display_name,
    c.therapist_name,
    ...c.additional_therapists.map((p) => p.name),
    ...sessionTherapists(c).map((p) => p.name),
  ];
}

/** The roster count: the view's own total, or "N of M" while the search
 *  or a pill narrows it. The noun follows the total. */
function rosterCountText(shown: number, total: number, narrowed: boolean): string {
  const noun = total === 1 ? 'client' : 'clients';
  return narrowed
    ? `${shown.toLocaleString()} of ${total.toLocaleString()} ${noun}`
    : `${total.toLocaleString()} ${noun}`;
}

/** The empty state when the search or a pill leaves nothing: it names
 *  what narrowed the list. `searched` is the trimmed query as typed. */
function rosterEmptyCopy(
  noun: RosterNoun,
  searched: string,
  pillActive: boolean,
): { headline: string; body: string } {
  if (searched !== '' && pillActive) {
    return { headline: `No ${noun} match “${searched}” with these filters.`, body: 'Clear the search or a filter to widen the list.' };
  }
  if (searched !== '') {
    return { headline: `No ${noun} match “${searched}”.`, body: 'Try another spelling, or a last name.' };
  }
  return { headline: `No ${noun} match these filters.`, body: 'Try clearing a filter to widen the list.' };
}

/** The empty state when the view itself has no clients. All clients
 *  offers the first add only to a viewer who may add a client. */
function rosterEmptyViewHeadline(view: RosterView, canAdd: boolean): string {
  if (view === 'spice') return 'No SPICE clients yet.';
  if (view === 'cm') return 'No care-management clients yet.';
  return canAdd ? 'Add the first client to start the roster.' : 'No clients yet.';
}

interface PickerOption {
  value: string;
  label: string;
}

/**
 * PickerSheet — single-select bottom sheet behind a FilterPill trigger
 * (the FilterBar FilterDropdown grammar reduced to radio rows; row
 * styling from SortControl). Big touch rows, readable words, Escape +
 * backdrop close via the BottomSheet primitive.
 */
function PickerSheet({
  title,
  isOpen,
  onClose,
  options,
  value,
  onSelect,
  testId,
}: {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  options: ReadonlyArray<PickerOption>;
  value: string;
  onSelect: (value: string) => void;
  testId: string;
}): ReactNode {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={title}>
      <div className="px-5 pt-2 pb-5" role="listbox" aria-label={title} data-testid={testId}>
        {options.map((o) => {
          const on = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={on}
              onClick={() => {
                onSelect(o.value);
                onClose();
              }}
              className={`flex w-full items-center gap-3 border-b border-sage-800/[8%] py-3 text-left text-sm last:border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-md ${
                on ? 'font-semibold text-ink' : 'font-medium text-ink'
              }`}
            >
              <span
                className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full ${
                  on ? 'bg-sage-800 text-cream' : 'border-[1.5px] border-sage-300'
                }`}
              >
                {on && <Check size={11} strokeWidth={2.4} aria-hidden="true" />}
              </span>
              <span className="flex-1">{o.label}</span>
            </button>
          );
        })}
      </div>
    </BottomSheet>
  );
}


/** The boxes of the dialog a refusal can read under, by their ids. */
const ADD_CLIENT_BOX_ID: Readonly<Record<RefusalBox, string>> = {
  first: 'add-client-first',
  last: 'add-client-last',
  preferred: 'add-client-preferred',
  service: 'add-client-service',
  cm: 'add-client-cm',
  therapist: 'add-client-therapist',
  pronouns: 'add-client-pronouns',
  rate_set: 'add-client-rate-set',
};

/**
 * Add client: first, last and preferred name, the service with its care
 * manager or SPICE therapist, pronouns and, for a seat that sets rates, a
 * standard rate set. A line offers an existing client of the same name,
 * active or archived. The route derives the display name from the parts.
 */
function AddClientDialog({ onClose }: { onClose: () => void }): ReactNode {
  const { tenantSlug, whoami } = useTenant();
  const navigate = useNavigate();
  const { careManagers, spiceTherapists } = useAssignOptions(tenantSlug);
  // The whole roster, archived included, read once when the dialog
  // opens: the page's own list holds active clients only under its
  // default filter, and an archived namesake is worth offering.
  const roster = useClientsList(tenantSlug, { includeArchived: true });
  const createClient = useCreateClient(tenantSlug);
  // A refused create reads as one sentence, naming the support contact
  // where one is needed.
  const contact = useSupportContact();
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [preferred, setPreferred] = useState('');
  const [engagement, setEngagement] = useState<ClientEngagementValue>('care_management');
  const [cmRosterId, setCmRosterId] = useState('');
  const [therapistRosterId, setTherapistRosterId] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [rateSetId, setRateSetId] = useState('');
  const [firstGap, setFirstGap] = useState(false);
  const [refusal, setRefusal] = useState<{ box: RefusalBox | null; text: string } | null>(null);

  // Standard rate sets (tenants.config billing.rate_sets): offered only
  // to a seat that may set client rates, and only when the tenant has
  // one to offer. Choosing one pre-fills the client's rates in the same
  // create; No rate set (the default) sends nothing extra.
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  const rateSets = readRateSets(tenantConfig?.config);
  const offerRateSets = canManageClientRates(whoami) && rateSets.length > 0;

  const showsCm = showsCareManager(engagement);
  const showsTherapist = showsSpiceTherapist(engagement);
  const shown = (box: RefusalBox): boolean =>
    box === 'cm' ? showsCm : box === 'therapist' ? showsTherapist : box === 'rate_set' ? offerRateSets : true;

  const duplicate = duplicateClient(roster.clients, first, last);

  /** The refusal's sentence when it reads under this box. */
  const boxError = (box: RefusalBox): string | undefined => (refusal?.box === box ? refusal.text : undefined);
  /** Drops the refusal shown under one of the given boxes. */
  const clearRefusal = (...boxes: RefusalBox[]): void => {
    setRefusal((prev) => (prev !== null && prev.box !== null && boxes.includes(prev.box) ? null : prev));
  };

  function submit(): void {
    if (createClient.isPending) return;
    if (first.trim() === '') {
      setFirstGap(true);
      focusFirstGap([ADD_CLIENT_BOX_ID.first]);
      return;
    }
    setRefusal(null);
    const body = addClientBody(
      { first, last, preferred, engagement, cmRosterId, therapistRosterId, pronouns, rateSetId },
      { offerRateSets, today: localDayOf(new Date().toISOString()) },
    );
    createClient.mutate(body, {
      onSuccess: (client) => {
        // The new client opens on its record.
        navigate(`/t/${tenantSlug}/clients/${client.id}`);
      },
      onError: (err) => {
        const read = clientCreateRefusal(err, contact, body);
        // A refusal for a box that is not shown reads on the alert line.
        const next = read.box !== null && !shown(read.box) ? { box: null, text: read.text } : read;
        setRefusal(next);
        if (next.box !== null) focusFirstGap([ADD_CLIENT_BOX_ID[next.box]]);
      },
    });
  }

  const onNameKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') submit();
  };

  return (
    <Dialog
      title="Add client"
      size="md"
      testId="add-client-dialog"
      initialFocusId={ADD_CLIENT_BOX_ID.first}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={createClient.isPending}
            onClick={submit}
            data-testid="add-client-submit"
          >
            Add client
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 py-2 sm:grid-cols-2">
        <Field
          label="First name"
          required
          htmlFor={ADD_CLIENT_BOX_ID.first}
          error={firstGap ? 'Fill in First name.' : boxError('first')}
        >
          <TextInput
            id={ADD_CLIENT_BOX_ID.first}
            data-testid={ADD_CLIENT_BOX_ID.first}
            className={FLD}
            nameCase="person"
            maxLength={100}
            value={first}
            onChange={(e) => {
              setFirstGap(false);
              clearRefusal('first');
              setFirst(e.target.value);
            }}
            onKeyDown={onNameKeyDown}
            {...fieldBoxAria(ADD_CLIENT_BOX_ID.first, { required: true, error: firstGap || refusal?.box === 'first' })}
          />
        </Field>
        <Field label="Last name" htmlFor={ADD_CLIENT_BOX_ID.last} error={boxError('last')}>
          <TextInput
            id={ADD_CLIENT_BOX_ID.last}
            data-testid={ADD_CLIENT_BOX_ID.last}
            className={FLD}
            nameCase="person"
            maxLength={100}
            value={last}
            onChange={(e) => {
              clearRefusal('last');
              setLast(e.target.value);
            }}
            onKeyDown={onNameKeyDown}
            {...fieldBoxAria(ADD_CLIENT_BOX_ID.last, { error: refusal?.box === 'last' })}
          />
        </Field>
        {duplicate !== null && (
          <p role="status" data-testid="add-client-duplicate" className="sm:col-span-2 text-sm text-ink">
            {`There is already a client named ${duplicate.display_name} (${duplicate.is_active ? 'active' : 'archived'}). `}
            <Link
              data-testid="add-client-duplicate-open"
              to={`/t/${tenantSlug}/clients/${duplicate.id}`}
              className="font-semibold text-sage-800 underline underline-offset-2"
            >
              {`Open ${pronounForms(duplicate.pronouns as ClientPronouns | null).possessive} record`}
            </Link>
            {' or keep going to add another.'}
          </p>
        )}
        <div className="sm:col-span-2">
          <Field label="Preferred name" htmlFor={ADD_CLIENT_BOX_ID.preferred} error={boxError('preferred')}>
            <TextInput
              id={ADD_CLIENT_BOX_ID.preferred}
              data-testid={ADD_CLIENT_BOX_ID.preferred}
              className={FLD}
              nameCase="person"
              maxLength={100}
              value={preferred}
              onChange={(e) => {
                clearRefusal('preferred');
                setPreferred(e.target.value);
              }}
              onKeyDown={onNameKeyDown}
              {...fieldBoxAria(ADD_CLIENT_BOX_ID.preferred, { error: refusal?.box === 'preferred' })}
            />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Service" htmlFor={ADD_CLIENT_BOX_ID.service} error={boxError('service')}>
            <SelectField
              size="field"
              id={ADD_CLIENT_BOX_ID.service}
              data-testid={ADD_CLIENT_BOX_ID.service}
              value={engagement}
              onChange={(e) => {
                clearRefusal('service', 'cm', 'therapist');
                setEngagement(e.target.value as ClientEngagementValue);
              }}
              {...fieldBoxAria(ADD_CLIENT_BOX_ID.service, { error: refusal?.box === 'service' })}
            >
              {(Object.keys(ENGAGEMENT_LABELS) as ClientEngagementValue[]).map((v) => (
                <option key={v} value={v}>
                  {ENGAGEMENT_LABELS[v]}
                </option>
              ))}
            </SelectField>
          </Field>
        </div>
        {showsCm && (
          <Field label="Care manager" htmlFor={ADD_CLIENT_BOX_ID.cm} error={boxError('cm')}>
            <SelectField
              size="field"
              id={ADD_CLIENT_BOX_ID.cm}
              data-testid={ADD_CLIENT_BOX_ID.cm}
              value={cmRosterId}
              onChange={(e) => {
                clearRefusal('cm');
                setCmRosterId(e.target.value);
              }}
              {...fieldBoxAria(ADD_CLIENT_BOX_ID.cm, { error: refusal?.box === 'cm' })}
            >
              <option value="">No care manager</option>
              {careManagers.map((cm) => (
                <option key={cm.roster_id} value={cm.roster_id}>
                  {cm.name}
                </option>
              ))}
            </SelectField>
          </Field>
        )}
        {showsTherapist && (
          <Field label="SPICE therapist" htmlFor={ADD_CLIENT_BOX_ID.therapist} error={boxError('therapist')}>
            <SelectField
              size="field"
              id={ADD_CLIENT_BOX_ID.therapist}
              data-testid={ADD_CLIENT_BOX_ID.therapist}
              value={therapistRosterId}
              onChange={(e) => {
                clearRefusal('therapist');
                setTherapistRosterId(e.target.value);
              }}
              {...fieldBoxAria(ADD_CLIENT_BOX_ID.therapist, { error: refusal?.box === 'therapist' })}
            >
              <option value="">No SPICE therapist</option>
              {spiceTherapists.map((t) => (
                <option key={t.roster_id} value={t.roster_id}>
                  {t.name}
                </option>
              ))}
            </SelectField>
          </Field>
        )}
        <Field label="Pronouns" htmlFor={ADD_CLIENT_BOX_ID.pronouns} error={boxError('pronouns')}>
          <SelectField
            size="field"
            id={ADD_CLIENT_BOX_ID.pronouns}
            data-testid={ADD_CLIENT_BOX_ID.pronouns}
            value={pronouns}
            onChange={(e) => {
              clearRefusal('pronouns');
              setPronouns(e.target.value);
            }}
            {...fieldBoxAria(ADD_CLIENT_BOX_ID.pronouns, { error: refusal?.box === 'pronouns' })}
          >
            <option value="">Not recorded</option>
            {CLIENT_PRONOUN_VALUES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </SelectField>
        </Field>
        {offerRateSets && (
          <Field
            label="Rate set"
            htmlFor={ADD_CLIENT_BOX_ID.rate_set}
            help="Sets their rates from a standard set, starting today."
            error={boxError('rate_set')}
          >
            <SelectField
              size="field"
              id={ADD_CLIENT_BOX_ID.rate_set}
              data-testid={ADD_CLIENT_BOX_ID.rate_set}
              value={rateSetId}
              onChange={(e) => {
                clearRefusal('rate_set');
                setRateSetId(e.target.value);
              }}
              {...fieldBoxAria(ADD_CLIENT_BOX_ID.rate_set, { help: true, error: refusal?.box === 'rate_set' })}
            >
              <option value="">No rate set</option>
              {rateSets.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </SelectField>
          </Field>
        )}
      </div>
      {refusal !== null && refusal.box === null && (
        <p role="alert" className="text-xs text-pepper-deep mt-1" data-testid="add-client-error">
          {refusal.text}
        </p>
      )}
    </Dialog>
  );
}

/** The engagement chip's copy is ENGAGEMENT_LABELS verbatim (VitalsBand.tsx)
 *  — the same words as the record header, so the two surfaces can never
 *  drift apart again. A care_management-only client gets NO chip: that
 *  is the quiet default, and SPICE involvement is the notable fact. */
const ENGAGEMENT_CHIP: Partial<Record<ClientEngagementValue, string>> = {
  spice: ENGAGEMENT_LABELS.spice,
  both: ENGAGEMENT_LABELS.both,
};

/** One piece of the row's meta line: plain text, or a caution ("this
 *  service has nobody assigned") that reads as attention, not grey. */
interface RowMetaPart {
  text: string;
  caution: boolean;
}

/**
 * The row's secondary line speaks the engagement, never the wrong service:
 * 'care_management' names every care manager, the lead first (+ the lead's
 * unit), or says so when there is none; 'spice' names every SPICE
 * therapist, the lead first, or says so, and never mentions a care manager
 * (a SPICE-only client has no care manager to report); 'both' shows both
 * halves on one line, each independently cautioned when missing.
 * A SPICE-only roster row with a stray primary_cm_roster_id (the
 * not-a-care-manager defect) is deliberately NOT read here — the fix is the
 * data repair, not a display-side mask.
 */
function clientRowMetaParts(
  client: Pick<
    ClientRosterRowWire,
    | 'engagement'
    | 'primary_cm_name'
    | 'unit_name'
    | 'spice_therapist_name'
    | 'additional_care_managers'
    | 'additional_spice_therapists'
  >,
): RowMetaPart[] {
  const cmNames = namesLeadFirst(client.primary_cm_name, client.additional_care_managers);
  const therapistNames = namesLeadFirst(client.spice_therapist_name, client.additional_spice_therapists);
  const cmPart: RowMetaPart[] =
    cmNames !== null
      ? [
          {
            text: client.unit_name ? `CM: ${cmNames} · ${client.unit_name}` : `CM: ${cmNames}`,
            caution: false,
          },
        ]
      : [{ text: 'No care manager assigned', caution: true }];
  const spicePart: RowMetaPart[] =
    therapistNames !== null
      ? [{ text: `SPICE: ${therapistNames}`, caution: false }]
      : [{ text: 'No SPICE therapist assigned', caution: true }];

  if (client.engagement === 'care_management') return cmPart;
  if (client.engagement === 'spice') return spicePart;
  return [...cmPart, ...spicePart];
}

/** The lead's name, then each additional person's, comma-joined; null
 *  when there is nobody. */
function namesLeadFirst(lead: string | null, additional: readonly { name: string }[]): string | null {
  const names = [lead, ...additional.map((p) => p.name)].filter((n): n is string => n !== null && n !== '');
  return names.length === 0 ? null : names.join(', ');
}

/** The row meta line: parts joined by the house middle dot, each part
 *  cautioned (pepper, medium weight) independently of its neighbors. */
function ClientRowMeta({ parts }: { parts: RowMetaPart[] }): ReactNode {
  const anyCaution = parts.some((p) => p.caution);
  return (
    <span
      className={[
        'mt-0.5 block text-xs',
        anyCaution && parts.length === 1 ? '' : 'truncate',
        anyCaution ? '' : 'text-ink-soft',
      ].join(' ')}
    >
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 ? <span aria-hidden="true"> · </span> : null}
          <span className={part.caution ? 'font-medium text-pepper-deep' : 'text-ink-soft'}>{part.text}</span>
        </Fragment>
      ))}
    </span>
  );
}

export function ClientRow({
  client,
  slug,
  hideCm = false,
}: {
  client: ClientRosterRowWire;
  slug: string;
  /** Suppress the "CM: <name>" secondary line — the care-manager view
   *  already scopes every row to one CM, so repeating it is noise there. */
  hideCm?: boolean;
}): ReactNode {
  const engagementChip = ENGAGEMENT_CHIP[client.engagement];
  const statusChip = lifecycleBadge(client.lifecycle_status ?? 'active', client.is_active);
  return (
    <Link
      to={`/t/${slug}/clients/${client.id}`}
      data-testid="client-row"
      className={[
        'flex items-center gap-3 px-4 py-3 border-b border-sage-800/[8%] last:border-b-0',
        'text-inherit no-underline hover:bg-cream/60 transition-colors duration-[var(--dur-base)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 focus-visible:ring-inset',
        client.is_active ? '' : 'opacity-70',
      ].join(' ')}
    >
      {/* 0142 (the humanity upgrade): the roster avatar carries the photo
          when one is on file (has_photo hint), lazily loaded from the serve
          endpoint; the initials monogram is the fallback (none / load error). */}
      <ClientAvatar
        name={client.display_name}
        photoUrl={client.has_photo ? clientPhotoUrl(slug, client.id) : null}
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-sage-800" title={client.display_name}>
          {client.display_name}
        </span>
        {hideCm ? null : <ClientRowMeta parts={clientRowMetaParts(client)} />}
      </span>
      <span className="flex shrink-0 items-center gap-1.5">
        {engagementChip && (
          <Chip variant="neutral" data-testid="engagement-chip">
            {engagementChip}
          </Chip>
        )}
        {/* The record header's badge, with no status note: Active (affirm),
            Inactive (caution), the deceased states (neutral), and Archived
            (neutral) whatever the lifecycle. */}
        <Chip variant={statusChip.variant} data-testid="status-chip">
          {statusChip.label}
        </Chip>
      </span>
      <span className="hidden sm:block w-[8.5rem] shrink-0 text-right text-xs text-ink-soft">
        <span className="block">Added {formatTimestamp(client.created_at)}</span>
        <span className="mt-0.5 block">Updated {formatTimestamp(client.updated_at)}</span>
      </span>
    </Link>
  );
}

// ---------------------------------------------------------------------
// The roster's three views, on the tab rail: All clients, Care management
// (engagement care_management or both) and SPICE (every active client
// whose engagement involves SPICE, across the whole roster, not only a
// therapist's own "My week"). The view lives in the URL (?view=cm,
// ?view=spice; none for All clients) so a link can land directly on it.
// ---------------------------------------------------------------------

type RosterView = 'all' | 'cm' | 'spice';

const VIEW_TABS: ReadonlyArray<{ value: RosterView; label: string }> = [
  { value: 'all', label: 'All clients' },
  { value: 'cm', label: 'Care management' },
  { value: 'spice', label: 'SPICE' },
];

/** How each view's empty state names its clients. */
type RosterNoun = 'clients' | 'care-management clients' | 'SPICE clients';
const VIEW_NOUN: Record<RosterView, RosterNoun> = {
  all: 'clients',
  cm: 'care-management clients',
  spice: 'SPICE clients',
};

/** The roster's URL state: the view alone; All clients is the bare path. */
function viewParams(next: RosterView): URLSearchParams {
  const sp = new URLSearchParams();
  if (next !== 'all') sp.set('view', next);
  return sp;
}

/**
 * The views a viewer's lane opens (ADR-0053, the server's clinicalScopeFor
 * in the viewer's terms): a viewer who reads every client
 * (canReadEveryClient) and a viewer on both lanes get all three; a
 * care-management lane gets All clients and Care management; a SPICE
 * lane gets SPICE alone.
 */
function rosterViewsFor(whoami: WhoamiResponse | null): readonly RosterView[] {
  if (canReadEveryClient(whoami)) return ['all', 'cm', 'spice'];
  const cm = whoami?.does_care_management === true;
  const spice = whoami?.does_spice === true;
  if (cm && spice) return ['all', 'cm', 'spice'];
  if (cm) return ['all', 'cm'];
  if (spice) return ['spice'];
  return ['all'];
}

/** A plain-roster client in the Care management view. */
function inCareManagementView(client: Pick<ClientRosterRowWire, 'engagement'>): boolean {
  return client.engagement === 'care_management' || client.engagement === 'both';
}

/** A plain-roster client in the SPICE view. */
function inSpiceView(client: Pick<ClientRosterRowWire, 'engagement'>): boolean {
  return client.engagement === 'spice' || client.engagement === 'both';
}

/** The plan-status bucket a row falls into (a client with no plan on
 *  file yet is its own bucket, never silently folded into "Active"). */
type SpicePlanStatusBucket = 'active' | 'covered' | 'on_hold' | 'no_schedule';

/** The Status filter's values: the plan-status buckets, plus the one
 *  record gap the office filters FOR — exactly the rows that carry the
 *  "No emergency contact" chip (spiceNeedsEmergencyContact below). */
type SpiceStatusFilter = SpicePlanStatusBucket | 'no_emergency_contact';

const SPICE_STATUS_OPTIONS: ReadonlyArray<{ value: SpiceStatusFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'covered', label: 'Covered' },
  { value: 'on_hold', label: 'Paused' },
  // The operational gaps an owner filters FOR (the CM_NONE precedent,
  // above): a SPICE client with a schedule nobody has set yet, and a
  // SPICE-only client with nobody to call.
  { value: 'no_schedule', label: 'No schedule set' },
  { value: 'no_emergency_contact', label: 'No emergency contact' },
];

const SPICE_SORT_OPTIONS: ReadonlyArray<{ value: SpiceRosterSortMode; label: string }> = [
  { value: 'therapist', label: 'Therapist' },
  { value: 'client', label: 'Client (A–Z)' },
  { value: 'last_session', label: 'Last session (most recent first)' },
];

/** The sentinel therapist-filter value for "no therapist assigned" —
 *  mirrors CM_NONE above. */
const THERAPIST_NONE = 'none';

/** The wire's snake_case sessions reshaped into spiceRosterShape's
 *  camelCase sort input, the one seam between the two. */
function toRosterSchedule(schedule: SpiceClientRosterScheduleWire | null): SpiceRosterSchedule | null {
  if (schedule === null) return null;
  return { sessions: schedule.sessions.map((s) => ({ weekday: s.weekday, startTime: s.start_time })) };
}

/** The row's away phrase ("Covered by Emerson Vale until 14 Oct",
 *  "Paused until 15 Sep (Vacation)"), or null while the plan reads
 *  regular. */
function spiceHoldChip(schedule: SpiceClientRosterScheduleWire | null, today: string): string | null {
  if (schedule === null) return null;
  const state = effectiveHold(
    schedule.status,
    schedule.hold_reason,
    schedule.status_until,
    { coverRosterId: schedule.cover_roster_id },
    today,
  );
  if (state.status === 'active') return null;
  return formatSpiceAway({
    state: state.status,
    hold_reason: state.hold_reason,
    hold_note: schedule.hold_note,
    status_until: schedule.status_until,
    cover_name: schedule.cover_name,
  });
}

/** 'active' / 'covered' / 'on_hold' / 'no_schedule': the status filter's
 *  bucket for a row (a client with no plan on file yet is its own bucket,
 *  never silently folded into "Active"). */
function spiceStatusBucket(client: Pick<SpiceClientRosterRowWire, 'schedule'>, today: string): SpicePlanStatusBucket {
  if (client.schedule === null) return 'no_schedule';
  const s = client.schedule;
  return effectiveHold(s.status, s.hold_reason, s.status_until, { coverRosterId: s.cover_roster_id }, today).status;
}

/** A SPICE-only client must have an emergency contact on file: the
 *  therapist visits alone and must be able to reach someone. The roster
 *  lists active clients only, so no is_active test is needed here. A
 *  client on both services is left alone — the care-management side
 *  carries its own expectations. Never a block: the row still opens. */
function spiceNeedsEmergencyContact(
  client: Pick<SpiceClientRosterRowWire, 'engagement' | 'has_emergency_contact'>,
): boolean {
  return client.engagement === 'spice' && !client.has_emergency_contact;
}

/** Every therapist, the lead first ("Jane Smith, Morgan Hale"); "No
 *  therapist assigned" when the client carries no SPICE therapist. A
 *  cover is named by the row's away chip while the plan reads covered. */
function spiceTherapistLabel(client: Pick<SpiceClientRosterRowWire, 'therapist_name' | 'additional_therapists'>): string {
  return namesLeadFirst(client.therapist_name, client.additional_therapists) ?? 'No therapist assigned';
}

/** The roster's column headings, aligned to SpiceClientRow's own zones
 *  (hidden on narrow screens exactly where the row hides those columns —
 *  a heading over a column that isn't there reads as a layout bug). */
function SpiceRosterHeadings(): ReactNode {
  return (
    <div
      className="hidden sm:flex items-center gap-3 px-4 py-2 text-label uppercase tracking-label font-semibold text-sage-400"
      data-testid="spice-roster-headings"
    >
      <span className="w-9 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">Therapist</span>
      <span className="w-[14rem] shrink-0">Schedule</span>
      <span className="w-[8rem] shrink-0 text-right">Last session</span>
      <span className="w-[9rem] shrink-0 text-right">This month</span>
    </div>
  );
}

function SpiceClientRow({
  client,
  slug,
  today,
}: {
  client: SpiceClientRosterRowWire;
  slug: string;
  today: string;
}): ReactNode {
  const holdChip = spiceHoldChip(client.schedule, today);
  return (
    <Link
      to={`/t/${slug}/clients/${client.id}`}
      data-testid="spice-client-row"
      className={[
        'flex items-center gap-3 px-4 py-3 border-b border-sage-800/[8%] last:border-b-0',
        'text-inherit no-underline hover:bg-cream/60 transition-colors duration-[var(--dur-base)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 focus-visible:ring-inset',
      ].join(' ')}
    >
      <ClientAvatar
        name={client.display_name}
        photoUrl={client.has_photo ? clientPhotoUrl(slug, client.id) : null}
      />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5" data-testid="spice-row-name-line">
          <span className="min-w-0 truncate text-sm font-semibold text-sage-800" title={client.display_name}>
            {client.display_name}
          </span>
          {spiceNeedsEmergencyContact(client) && (
            // A quiet chip in the clinical-warning ink, beside the name:
            // a safety fact, never a banner and never a block.
            <Chip variant="clinical-warn" className="shrink-0" data-testid="spice-row-no-emergency-contact">
              No emergency contact
            </Chip>
          )}
        </span>
        <span className="mt-0.5 block truncate text-xs text-ink-soft" data-testid="spice-row-therapist">
          {spiceTherapistLabel(client)}
          {client.unit_name && <span aria-hidden="true"> · {client.unit_name}</span>}
        </span>
      </span>
      <span
        className="hidden sm:flex w-[14rem] shrink-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-ink-soft"
        data-testid="spice-row-schedule"
      >
        <SpiceScheduleSummary sessions={client.schedule?.sessions ?? []} className="min-w-0" />
        {holdChip && (
          <Chip variant="neutral" data-testid="spice-row-status-chip">
            {holdChip}
          </Chip>
        )}
      </span>
      <span
        className="hidden sm:block w-[8rem] shrink-0 text-right text-xs text-ink-soft"
        data-testid="spice-row-last-session"
      >
        {formatSpiceLastSession(client.last_session_occurred_on, client.last_declined_occurred_on)}
      </span>
      <span
        className="hidden sm:block w-[9rem] shrink-0 text-right text-xs text-ink-soft"
        data-testid="spice-row-sessions-this-month"
      >
        {formatSpiceThisMonth(client.sessions_this_month, client.declines_this_month)}
      </span>
    </Link>
  );
}

type PickerKey = 'cm' | 'unit' | 'status' | 'sort';
type SpicePickerKey = 'spice-therapist' | 'spice-status' | 'spice-sort';

export function Clients(): ReactNode {
  const { tenantSlug, whoami } = useTenant();
  // Reads only the cached tenant config: no call of its own for a person
  // the page turns away.
  const contact = useSupportContact({ enabled: false });
  // The office width or the engagement lane (see the header note).
  const allowed = canViewAnyClientRoster(whoami);
  // The door back (R9 companion): a viewer who has a caseload gets a quiet
  // way home to "Your day" — the ClientsLandingGate would send them there
  // anyway, this just saves the round trip through the gate.
  const showYourDayLink = whoami?.has_caseload === true;
  const [addOpen, setAddOpen] = useState(false);

  // The view lives in the URL (?view=cm, ?view=spice) so a link can land
  // directly on it; All clients is the no-param state. A view the lane
  // does not open falls back to the lane's first (SPICE, for a SPICE-only
  // lane).
  const [searchParams, setSearchParams] = useSearchParams();
  const views = rosterViewsFor(whoami);
  const viewParam = searchParams.get('view');
  const requestedView: RosterView = viewParam === 'spice' ? 'spice' : viewParam === 'cm' ? 'cm' : 'all';
  const view: RosterView = views.includes(requestedView) ? requestedView : views[0]!;
  const plainView = view !== 'spice';
  function setView(next: RosterView): void {
    setSearchParams(viewParams(next), { replace: true });
  }
  // A ?q= in a link is from before the search left the URL; it is dropped
  // with a replace, so the address bar shows the view alone.
  useEffect(() => {
    if (searchParams.has('q')) setSearchParams(viewParams(view), { replace: true });
  }, [searchParams, setSearchParams, view]);
  const [today] = useState(() => practiceTodayIso());

  // The name search: one value for every view, in component state only.
  const [search, setSearch] = useState('');
  const query = searchQuery(search);
  const searchActive = query !== '';
  // A page restored from the back-forward cache keeps its state, so a
  // search typed before leaving the site would come back with it. The
  // filters and sort keep theirs; the rule is about the search.
  useEffect(() => {
    function onPageShow(e: PageTransitionEvent): void {
      if (e.persisted) setSearch('');
    }
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Sort + filter state (client-side; see the header note). Status
  // replaced the include-archived pill: Archived/All fetch the wider
  // list and narrow locally.
  const [status, setStatus] = useState<StatusFilter>('active');
  const [cmFilter, setCmFilter] = useState(''); // '' = all; CM_NONE = unassigned; else roster_id
  const [unitFilter, setUnitFilter] = useState(''); // '' = all; else unit name
  const [sort, setSort] = useState<SortMode>('name');
  const [openPicker, setOpenPicker] = useState<PickerKey | null>(null);

  const { clients, isLoading, isError, error, refetch } = useClientsList(tenantSlug, {
    includeArchived: status !== 'active',
    enabled: allowed && plainView,
  });

  // SPICE view state + data (client-side sort/filter, the plain-view
  // precedent): therapist / status filters, therapist-then-weekday/time
  // default sort.
  const [spiceTherapistFilter, setSpiceTherapistFilter] = useState(''); // '' = all; THERAPIST_NONE; else roster_id
  const [spiceStatusFilter, setSpiceStatusFilter] = useState<SpiceStatusFilter | ''>('');
  const [spiceSort, setSpiceSort] = useState<SpiceRosterSortMode>('therapist');
  const [openSpicePicker, setOpenSpicePicker] = useState<SpicePickerKey | null>(null);

  const {
    clients: spiceClients,
    isLoading: spiceIsLoading,
    isError: spiceIsError,
    error: spiceError,
    refetch: spiceRefetch,
  } = useSpiceClientsList(tenantSlug, { enabled: allowed && view === 'spice' });

  const spiceTherapistOptions = useMemo<PickerOption[]>(() => {
    const byId = new Map<string, string>();
    for (const c of spiceClients) {
      if (c.therapist_roster_id && c.therapist_name) byId.set(c.therapist_roster_id, c.therapist_name);
      for (const p of c.additional_therapists) byId.set(p.roster_id, p.name);
      for (const p of sessionTherapists(c)) byId.set(p.roster_id, p.name);
    }
    const named = [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      { value: '', label: 'All therapists' },
      { value: THERAPIST_NONE, label: 'No therapist assigned' },
      ...named,
    ];
  }, [spiceClients]);

  const spiceVisible = useMemo(() => {
    const filtered = spiceClients.filter((c) => {
      if (!matchesSearch(query, spiceSearchFields(c))) return false;
      if (
        spiceTherapistFilter === THERAPIST_NONE &&
        (c.therapist_roster_id !== null || c.additional_therapists.length > 0)
      ) {
        return false;
      }
      if (
        spiceTherapistFilter &&
        spiceTherapistFilter !== THERAPIST_NONE &&
        c.therapist_roster_id !== spiceTherapistFilter &&
        !c.additional_therapists.some((p) => p.roster_id === spiceTherapistFilter) &&
        !sessionTherapists(c).some((p) => p.roster_id === spiceTherapistFilter)
      ) {
        return false;
      }
      if (spiceStatusFilter === 'no_emergency_contact') {
        if (!spiceNeedsEmergencyContact(c)) return false;
      } else if (spiceStatusFilter && spiceStatusBucket(c, today) !== spiceStatusFilter) {
        return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) =>
      compareSpiceRosterRows(
        {
          display_name: a.display_name,
          therapist_name: a.therapist_name,
          last_session_occurred_on: a.last_session_occurred_on,
          schedule: toRosterSchedule(a.schedule),
        },
        {
          display_name: b.display_name,
          therapist_name: b.therapist_name,
          last_session_occurred_on: b.last_session_occurred_on,
          schedule: toRosterSchedule(b.schedule),
        },
        spiceSort,
      ),
    );
  }, [spiceClients, query, spiceTherapistFilter, spiceStatusFilter, spiceSort, today]);

  // The pills are what Clear filters owns; the search has its own clear.
  const spicePillActive = spiceTherapistFilter !== '' || spiceStatusFilter !== '';
  function clearSpiceFilters(): void {
    setSpiceTherapistFilter('');
    setSpiceStatusFilter('');
  }
  const spiceTherapistLabelText =
    spiceTherapistFilter === ''
      ? 'Therapist'
      : spiceTherapistFilter === THERAPIST_NONE
        ? 'Therapist: none assigned'
        : `Therapist: ${spiceTherapistOptions.find((o) => o.value === spiceTherapistFilter)?.label ?? 'selected'}`;
  const spiceStatusLabelText =
    spiceStatusFilter === ''
      ? 'Status'
      : `Status: ${SPICE_STATUS_OPTIONS.find((o) => o.value === spiceStatusFilter)?.label ?? spiceStatusFilter}`;
  const spiceSortLabelText = `Sort: ${SPICE_SORT_OPTIONS.find((o) => o.value === spiceSort)?.label ?? spiceSort}`;

  // Filter options derive from the loaded roster itself — the pickers
  // only offer people/units that actually appear (FilterBar's
  // single-source-of-truth discipline).
  const cmOptions = useMemo<PickerOption[]>(() => {
    const byId = new Map<string, string>();
    for (const c of clients) {
      if (c.primary_cm_roster_id && c.primary_cm_name) {
        byId.set(c.primary_cm_roster_id, c.primary_cm_name);
      }
      for (const p of c.additional_care_managers) byId.set(p.roster_id, p.name);
    }
    const named = [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      { value: '', label: 'All care managers' },
      { value: CM_NONE, label: 'No care manager assigned' },
      ...named,
    ];
  }, [clients]);

  const unitOptions = useMemo<PickerOption[]>(() => {
    const names = [...new Set(clients.map((c) => c.unit_name).filter((n): n is string => n !== null))].sort(
      (a, b) => a.localeCompare(b),
    );
    return [{ value: '', label: 'All units' }, ...names.map((n) => ({ value: n, label: n }))];
  }, [clients]);

  // The views' counts, from whichever list is loaded: the plain roster
  // answers all three (its active rows, split by engagement); on the
  // SPICE view, the SPICE list answers its own.
  const activeClients = useMemo(() => clients.filter((c) => c.is_active), [clients]);
  const viewCounts: Partial<Record<RosterView, number>> = plainView
    ? isLoading
      ? {}
      : {
          all: activeClients.length,
          cm: activeClients.filter(inCareManagementView).length,
          spice: activeClients.filter(inSpiceView).length,
        }
    : spiceIsLoading
      ? {}
      : { spice: spiceClients.length };

  // The view's own rows, before the filters narrow them: every client on
  // All clients, care management and both on Care management. The count
  // and the empty state read these, never the whole plain roster.
  const viewClients = useMemo(
    () => (view === 'cm' ? clients.filter(inCareManagementView) : clients),
    [clients, view],
  );

  const visible = useMemo(() => {
    const filtered = viewClients.filter((c) => {
      if (!matchesSearch(query, clientSearchFields(c))) return false;
      if (status === 'active' && !c.is_active) return false;
      if (status === 'archived' && c.is_active) return false;
      if (cmFilter === CM_NONE && (c.primary_cm_roster_id !== null || c.additional_care_managers.length > 0)) {
        return false;
      }
      if (cmFilter && cmFilter !== CM_NONE && !clientHasCareManager(c, cmFilter)) return false;
      if (unitFilter && c.unit_name !== unitFilter) return false;
      return true;
    });
    const sorted = [...filtered];
    if (sort === 'added') sorted.sort((a, b) => b.created_at.localeCompare(a.created_at));
    else if (sort === 'updated') sorted.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    else sorted.sort(byDisplayName);
    return sorted;
  }, [viewClients, query, status, cmFilter, unitFilter, sort]);

  const pillActive = status !== 'active' || cmFilter !== '' || unitFilter !== '';

  function clearFilters(): void {
    setStatus('active');
    setCmFilter('');
    setUnitFilter('');
  }

  const canAddClient = canManageClientIdentity(whoami);
  const searched = search.trim();

  // Pill labels carry the current choice in words (the audience ruling:
  // readable words, not icons — you can see what's filtered at a glance).
  const cmLabel =
    cmFilter === ''
      ? 'Care manager'
      : cmFilter === CM_NONE
        ? 'CM: none assigned'
        : `CM: ${cmOptions.find((o) => o.value === cmFilter)?.label ?? 'selected'}`;
  const unitLabel = unitFilter === '' ? 'Unit' : `Unit: ${unitFilter}`;
  const statusLabel = `Status: ${STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status}`;
  const sortLabel = `Sort: ${SORT_OPTIONS.find((o) => o.value === sort)?.label ?? sort}`;

  const deniedContent = (
    <>
      <div
        role="alert"
        className="my-3 p-3 border border-warm-300 bg-warm-50 rounded-card text-sm text-ink"
      >
        {contactSentence(
          "The full client list isn't part of your access yet. Your own clients are on My Clients, and {contact_admin} can widen your access.",
          contact,
        )}
      </div>
      <Link
        to={`/t/${tenantSlug}/hub`}
        className="inline-block mt-4 min-h-11 text-sm text-sage-700 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      >
        Back to home
      </Link>
    </>
  );

  return (
    <PageShell
      eyebrow="Clients"
      title="Client roster"
      maxWidth="wide"
      testId="clients-page"
      access={{ allowed, deniedContent }}
      actions={
        <>
          {showYourDayLink && (
            <NavArrowLink to={`/t/${tenantSlug}/clients`} data-testid="your-day-link">
              My Clients
            </NavArrowLink>
          )}
          {canAddClient && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setAddOpen(true)}
              data-testid="add-client-action"
            >
              <Plus size={15} aria-hidden="true" />
              Add client
            </Button>
          )}
        </>
      }
    >
      {/* The tab row: the view rail, then the search on the rail's line,
          right-aligned; below the sm breakpoint the search wraps under the
          tabs at full width. The rail is the row's only growing item, and
          min-w-0 lets it shrink to the row so the scroller inside it
          scrolls in place on a narrow screen, never the page: the scroller
          wraps the tablist and the edge fade sits outside it
          (ClientRecordTabs' rail, the same pattern). */}
      <div data-testid="roster-tab-row" className="mb-3 flex flex-wrap items-end gap-x-4 gap-y-2">
        <div data-testid="roster-view-rail" className="relative grow min-w-0">
          <div className="overflow-x-auto">
            <div
              role="tablist"
              aria-label="View"
              data-testid="roster-view-tabs"
              className={`flex w-full min-w-max gap-1 ${TAB_RAIL_CLASSES.tablist}`}
            >
              {VIEW_TABS.filter((tab) => views.includes(tab.value)).map((tab) => {
                const isActive = tab.value === view;
                const count = viewCounts[tab.value];
                return (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setView(tab.value)}
                    data-testid={`roster-view-${tab.value}`}
                    className={
                      `${TAB_RAIL_CLASSES.tab} ` +
                      (isActive
                        ? `border-warm-400 ${TAB_RAIL_CLASSES.active}`
                        : TAB_RAIL_CLASSES.inactive)
                    }
                  >
                    {tab.label}
                    {count !== undefined && <span className="ml-1.5 text-ink-soft">{count}</span>}
                  </button>
                );
              })}
            </div>
          </div>
          <div
            data-testid="tab-rail-fade"
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-cream to-transparent lg:hidden"
          />
        </div>
        <SearchField
          value={search}
          onChange={setSearch}
          aria-label="Search clients"
          placeholder="Search clients"
          testId="clients-search"
          className="w-full sm:ml-auto sm:w-80"
        />
      </div>

      {view === 'spice' && (
        <>
          <div data-testid="roster-filter-row" className="mb-3 flex flex-wrap items-center gap-2">
            <FilterPill
              label={spiceTherapistLabelText}
              active={spiceTherapistFilter !== ''}
              onClick={() => setOpenSpicePicker('spice-therapist')}
              data-testid="spice-therapist-filter"
            />
            <FilterPill
              label={spiceStatusLabelText}
              active={spiceStatusFilter !== ''}
              onClick={() => setOpenSpicePicker('spice-status')}
              data-testid="spice-status-filter"
            />
            {spicePillActive && (
              <Button variant="ghost" size="sm" onClick={clearSpiceFilters} data-testid="spice-clear-filters">
                <X size={13} aria-hidden="true" /> Clear filters
              </Button>
            )}
            <span className="ml-auto flex items-center gap-3">
              <Link
                to={`/t/${tenantSlug}/team?tab=spice_week`}
                className="text-xs text-sage-700 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight"
                data-testid="spice-week-grid-link"
              >
                This week&rsquo;s grid →
              </Link>
              <FilterPill
                label={spiceSortLabelText}
                onClick={() => setOpenSpicePicker('spice-sort')}
                data-testid="spice-sort-control"
              />
              <span className="text-xs text-ink-soft" data-testid="spice-clients-count">
                {rosterCountText(spiceVisible.length, spiceClients.length, searchActive || spicePillActive)}
              </span>
            </span>
          </div>

          <AsyncBoundary
            isLoading={spiceIsLoading}
            isError={spiceIsError}
            error={spiceError}
            onRetry={spiceRefetch}
            isEmpty={!spiceIsLoading && !spiceIsError && spiceVisible.length === 0}
            emptyHeadline={
              spiceClients.length === 0
                ? rosterEmptyViewHeadline(view, canAddClient)
                : rosterEmptyCopy(VIEW_NOUN[view], searched, spicePillActive).headline
            }
            emptyBody={
              spiceClients.length === 0 ? '' : rosterEmptyCopy(VIEW_NOUN[view], searched, spicePillActive).body
            }
          >
            <div
              className="rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] overflow-hidden"
              data-testid="spice-clients-list"
            >
              <SpiceRosterHeadings />
              {spiceVisible.map((c) => (
                <SpiceClientRow key={c.id} client={c} slug={tenantSlug} today={today} />
              ))}
            </div>
          </AsyncBoundary>

          <PickerSheet
            title="Therapist"
            isOpen={openSpicePicker === 'spice-therapist'}
            onClose={() => setOpenSpicePicker(null)}
            options={spiceTherapistOptions}
            value={spiceTherapistFilter}
            onSelect={setSpiceTherapistFilter}
            testId="spice-therapist-filter-sheet"
          />
          <PickerSheet
            title="Status"
            isOpen={openSpicePicker === 'spice-status'}
            onClose={() => setOpenSpicePicker(null)}
            options={[{ value: '', label: 'All statuses' }, ...SPICE_STATUS_OPTIONS]}
            value={spiceStatusFilter}
            onSelect={(v) => setSpiceStatusFilter(v as SpiceStatusFilter | '')}
            testId="spice-status-filter-sheet"
          />
          <PickerSheet
            title="Sort"
            isOpen={openSpicePicker === 'spice-sort'}
            onClose={() => setOpenSpicePicker(null)}
            options={SPICE_SORT_OPTIONS}
            value={spiceSort}
            onSelect={(v) => setSpiceSort(v as SpiceRosterSortMode)}
            testId="spice-sort-sheet"
          />
        </>
      )}

      {plainView && (
      <>
      <div data-testid="roster-filter-row" className="mb-3 flex flex-wrap items-center gap-2">
        <FilterPill
          label={cmLabel}
          active={cmFilter !== ''}
          onClick={() => setOpenPicker('cm')}
          data-testid="cm-filter"
        />
        <FilterPill
          label={unitLabel}
          active={unitFilter !== ''}
          onClick={() => setOpenPicker('unit')}
          data-testid="unit-filter"
        />
        <FilterPill
          label={statusLabel}
          active={status !== 'active'}
          onClick={() => setOpenPicker('status')}
          data-testid="status-filter"
        />
        {pillActive && (
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters">
            <X size={13} aria-hidden="true" /> Clear filters
          </Button>
        )}
        <span className="ml-auto flex min-w-0 flex-wrap items-center gap-2">
          <FilterPill label={sortLabel} onClick={() => setOpenPicker('sort')} data-testid="sort-control" />
          <span className="text-xs text-ink-soft" data-testid="clients-count">
            {rosterCountText(visible.length, viewClients.length, searchActive || pillActive)}
          </span>
        </span>
      </div>

      <AsyncBoundary
        isLoading={isLoading}
        isError={isError}
        error={error}
        onRetry={refetch}
        isEmpty={!isLoading && !isError && visible.length === 0}
        emptyHeadline={
          viewClients.length === 0
            ? rosterEmptyViewHeadline(view, canAddClient)
            : rosterEmptyCopy(VIEW_NOUN[view], searched, pillActive).headline
        }
        emptyBody={viewClients.length === 0 ? '' : rosterEmptyCopy(VIEW_NOUN[view], searched, pillActive).body}
      >
        <div
          className="rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] overflow-hidden"
          data-testid="clients-list"
        >
          {visible.map((c) => (
            <ClientRow key={c.id} client={c} slug={tenantSlug} />
          ))}
        </div>
      </AsyncBoundary>

      <PickerSheet
        title="Care manager"
        isOpen={openPicker === 'cm'}
        onClose={() => setOpenPicker(null)}
        options={cmOptions}
        value={cmFilter}
        onSelect={setCmFilter}
        testId="cm-filter-sheet"
      />
      <PickerSheet
        title="Unit"
        isOpen={openPicker === 'unit'}
        onClose={() => setOpenPicker(null)}
        options={unitOptions}
        value={unitFilter}
        onSelect={setUnitFilter}
        testId="unit-filter-sheet"
      />
      <PickerSheet
        title="Status"
        isOpen={openPicker === 'status'}
        onClose={() => setOpenPicker(null)}
        options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        value={status}
        onSelect={(v) => setStatus(v as StatusFilter)}
        testId="status-filter-sheet"
      />
      <PickerSheet
        title="Sort"
        isOpen={openPicker === 'sort'}
        onClose={() => setOpenPicker(null)}
        options={SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        value={sort}
        onSelect={(v) => setSort(v as SortMode)}
        testId="sort-sheet"
      />
      </>
      )}

      {addOpen && <AddClientDialog onClose={() => setAddOpen(false)} />}
    </PageShell>
  );
}
